import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  DatePicker,
  Select,
  Tag,
  Space,
  Typography,
  Row,
  Col,
  Statistic,
  Modal,
  Descriptions,
  message
} from 'antd';
import {
  EyeOutlined,
  DownloadOutlined,
  QrcodeOutlined,
  BankOutlined,
  WalletOutlined,
  DollarOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  SendOutlined
} from '@ant-design/icons';
import axios from 'axios';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;

const MerchantCashIn = () => {
  const [loading, setLoading] = useState(false);
  const [transactions, setTransactions] = useState([]);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 0,
    totalItems: 0,
    itemsPerPage: 20
  });
  const [filters, setFilters] = useState({
    status: '',
    payment_method_type: '',
    date_from: null,
    date_to: null
  });
  const [summary, setSummary] = useState({
    total_amount: 0,
    total_transactions: 0,
    total_fee: 0,
    success_rate: 0
  });

  // Modals
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);

  // Load data on component mount
  useEffect(() => {
    fetchTransactions();
    fetchSummary();
  }, []);

  // Fetch transactions
  const fetchTransactions = async (page = 1, currentFilters = filters) => {
    try {
      setLoading(true);
      const params = {
        page,
        limit: 20,
        ...currentFilters,
        date_from: currentFilters.date_from ? dayjs(currentFilters.date_from).format('YYYY-MM-DD') : '',
        date_to: currentFilters.date_to ? dayjs(currentFilters.date_to).format('YYYY-MM-DD') : ''
      };

      const token = localStorage.getItem('merchant_access_token');
      const response = await axios.get('/api/cash-in/transactions', {
        params,
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.data) {
        setTransactions(response.data.transactions || []);
        setPagination(response.data.pagination);
      }
    } catch (error) {
      console.error('Error fetching transactions:', error);
      message.error('Failed to fetch transactions');
    } finally {
      setLoading(false);
    }
  };

  // Fetch summary
  const fetchSummary = async () => {
    try {
      const token = localStorage.getItem('merchant_access_token');
      const response = await axios.get('/api/cash-in/summary', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      setSummary(response.data);
    } catch (error) {
      console.error('Error fetching summary:', error);
    }
  };

  // Handle filter changes
  const handleFilterChange = (newFilters) => {
    const updatedFilters = { ...filters, ...newFilters };
    setFilters(updatedFilters);
    fetchTransactions(1, updatedFilters);
  };

  // Handle pagination
  const handleTableChange = (paginationInfo) => {
    fetchTransactions(paginationInfo.current);
  };

  // View transaction details
  const viewTransactionDetails = (transaction) => {
    setSelectedTransaction(transaction);
    setDetailModalVisible(true);
  };

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR'
    }).format(amount);
  };

  // Get status tag
  const getStatusTag = (status) => {
    const statusConfig = {
      pending: { color: 'orange', icon: <ClockCircleOutlined /> },
      completed: { color: 'green', icon: <CheckCircleOutlined /> },
      failed: { color: 'red', icon: <ExclamationCircleOutlined /> },
      expired: { color: 'default', icon: <ExclamationCircleOutlined /> }
    };

    const config = statusConfig[status] || { color: 'default', icon: null };

    return (
      <Tag color={config.color} icon={config.icon}>
        {status?.toUpperCase()}
      </Tag>
    );
  };

  // Get payment method icon
  const getPaymentMethodIcon = (type) => {
    switch (type) {
      case 'qris':
        return <QrcodeOutlined />;
      case 'va':
        return <BankOutlined />;
      case 'ewallet':
        return <WalletOutlined />;
      default:
        return <DollarOutlined />;
    }
  };

  // Table columns
  const columns = [
    {
      title: 'Transaction ID',
      dataIndex: 'transaction_id',
      key: 'transaction_id',
      width: 180,
      render: (text) => (
        <Text code style={{ fontSize: '12px' }}>{text}</Text>
      )
    },
    {
      title: 'Payment Method',
      dataIndex: 'payment_method_type',
      key: 'payment_method_type',
      width: 140,
      render: (type, record) => (
        <div>
          <Space>
            {getPaymentMethodIcon(type)}
            <span>{type?.toUpperCase()}</span>
          </Space>
          {type === 'qris' && record.qris_data && (
            <div style={{ fontSize: '12px', color: '#666' }}>
              NMID: {record.qris_data.merchant_id}
            </div>
          )}
          {type === 'va' && record.va_data && (
            <div style={{ fontSize: '12px', color: '#666' }}>
              {record.va_data.bank_name}: {record.va_data.account_number}
            </div>
          )}
          {type === 'ewallet' && record.ewallet_data && (
            <div style={{ fontSize: '12px', color: '#666' }}>
              {record.ewallet_data.provider}: {record.ewallet_data.account_number}
            </div>
          )}
        </div>
      )
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
      width: 120,
      align: 'right',
      render: (amount) => (
        <Text strong style={{ color: '#1890ff' }}>
          {formatCurrency(amount)}
        </Text>
      )
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status) => getStatusTag(status)
    },
    {
      title: 'Created',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 120,
      render: (date) => dayjs(date).format('DD/MM/YY HH:mm')
    },
    {
      title: 'Completed',
      dataIndex: 'completed_at',
      key: 'completed_at',
      width: 120,
      render: (date) => date ? dayjs(date).format('DD/MM/YY HH:mm') : '-'
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 80,
      render: (_, record) => (
        <Space size="small">
          <Button
            type="link"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => viewTransactionDetails(record)}
          />
        </Space>
      )
    }
  ];

  return (
    <div style={{ padding: '24px' }}>
      <Title level={2} style={{ marginBottom: 24 }}>
        Cash In Transactions
      </Title>

      {/* Summary Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} md={6} lg={6}>
          <Card>
            <Statistic
              title="Total Transaksi"
              value={summary.total_transactions || 0}
              prefix={<SendOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6} lg={6}>
          <Card>
            <Statistic
              title="Total Nominal"
              value={summary.total_amount || 0}
              prefix="Rp"
              precision={0}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6} lg={6}>
          <Card>
            <Statistic
              title="Total Fee"
              value={summary.total_fee || 0}
              prefix="Rp"
              precision={0}
              valueStyle={{ color: '#faad14' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6} lg={6}>
          <Card>
            <Statistic
              title="Success Rate"
              value={summary.success_rate || 0}
              suffix="%"
              precision={1}
              valueStyle={{ color: summary.success_rate >= 90 ? '#52c41a' : '#faad14' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Filters and Data Table */}
      <Card
        title="Transaction History"
        extra={
          <Space>
            <Button icon={<DownloadOutlined />}>
              Export CSV
            </Button>
          </Space>
        }
      >
        {/* Filters */}
        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col span={6}>
            <Select
              placeholder="Payment Method"
              value={filters.payment_method_type}
              onChange={(value) => handleFilterChange({ payment_method_type: value })}
              style={{ width: '100%' }}
            >
              <Option value="">All Methods</Option>
              <Option value="qris">QRIS</Option>
              <Option value="va">Virtual Account</Option>
              <Option value="ewallet">E-Wallet</Option>
            </Select>
          </Col>
          <Col span={6}>
            <Select
              placeholder="Status"
              value={filters.status}
              onChange={(value) => handleFilterChange({ status: value })}
              style={{ width: '100%' }}
            >
              <Option value="">All Status</Option>
              <Option value="pending">Pending</Option>
              <Option value="completed">Completed</Option>
              <Option value="failed">Failed</Option>
              <Option value="expired">Expired</Option>
            </Select>
          </Col>
          <Col span={12}>
            <RangePicker
              placeholder={['From Date', 'To Date']}
              value={filters.date_from && filters.date_to ? [dayjs(filters.date_from), dayjs(filters.date_to)] : []}
              onChange={(dates) => {
                handleFilterChange({
                  date_from: dates?.[0] || null,
                  date_to: dates?.[1] || null
                });
              }}
              style={{ width: '100%' }}
            />
          </Col>
        </Row>

        {/* Table */}
        <Table
          columns={columns}
          dataSource={transactions}
          rowKey="id"
          loading={loading}
          pagination={{
            current: pagination.currentPage,
            pageSize: pagination.itemsPerPage,
            total: pagination.totalItems,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} transactions`
          }}
          onChange={handleTableChange}
          scroll={{ x: 1000 }}
        />
      </Card>

      {/* Transaction Detail Modal */}
      <Modal
        title="Transaction Details"
        open={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        footer={null}
        width={800}
      >
        {selectedTransaction && (
          <Descriptions bordered column={2}>
            <Descriptions.Item label="Transaction ID" span={2}>
              <Text code>{selectedTransaction.transaction_id}</Text>
            </Descriptions.Item>
            <Descriptions.Item label="External ID">
              {selectedTransaction.external_id || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Payment Method">
              <Space>
                {getPaymentMethodIcon(selectedTransaction.payment_method_type)}
                {selectedTransaction.payment_method_type?.toUpperCase()}
              </Space>
            </Descriptions.Item>
            <Descriptions.Item label="Amount">
              <Text strong style={{ color: '#1890ff' }}>
                {formatCurrency(selectedTransaction.amount)}
              </Text>
            </Descriptions.Item>
            <Descriptions.Item label="Fee">
              {formatCurrency(selectedTransaction.fee || 0)}
            </Descriptions.Item>
            <Descriptions.Item label="Status" span={2}>
              {getStatusTag(selectedTransaction.status)}
            </Descriptions.Item>
            <Descriptions.Item label="Description" span={2}>
              {selectedTransaction.description || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Created At">
              {dayjs(selectedTransaction.created_at).format('DD/MM/YYYY HH:mm:ss')}
            </Descriptions.Item>
            <Descriptions.Item label="Completed At">
              {selectedTransaction.completed_at
                ? dayjs(selectedTransaction.completed_at).format('DD/MM/YYYY HH:mm:ss')
                : '-'
              }
            </Descriptions.Item>
          </Descriptions>
        )}
      </Modal>
    </div>
  );
};

export default MerchantCashIn;